import { useState, useEffect } from 'react';
import { useAuth } from '../firebase/useAuth';
import { getAPIUrl } from '../../config/api.config';

// API Response Types
interface MarketStats {
  avgPipsPerSignal: number;
  avgWinRate: {
    free: number;
    professional: number;
    enterprise: number;
  };
  pipValue: number;
  tradingDaysPerMonth: number;
  indonesianMarketHours: {
    asiaSession: string;
    europeSession: string;
    americaSession: string;
  };
}

interface ConversionData {
  freeToProUpgrade: {
    conversionRate: number;
    avgTimeToConvert: number;
    mainReasons: string[];
  };
  proToEnterpriseUpgrade: {
    conversionRate: number;
    avgTimeToConvert: number;
    mainReasons: string[];
  };
}

interface PaymentMethod {
  name: string;
  usage: number;
  type: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  period: string;
  features: string[];
  limits: {
    signalsPerDay: number;
    instruments: number;
  };
}

interface TierComparisonData {
  plans: SubscriptionPlan[];
  marketStats: MarketStats;
  conversionData: ConversionData;
  paymentMethods: PaymentMethod[];
  timestamp: string;
  currency: string;
  locale: string;
}

interface ROICalculation {
  planId: string;
  planName: string;
  planCost: number;
  inputs: {
    accountSize: number;
    riskPerTrade: number;
    tradingDays: number;
  };
  calculations: {
    monthlySignals: number;
    expectedWins: number;
    winRate: number;
    lotSize: number;
    profitPerWin: number;
    expectedProfit: number;
    expectedROI: number;
    profitAfterCost: number;
    breakEvenDays: number;
  };
  analysis: {
    isBreakEvenAchievable: boolean;
    riskLevel: 'low' | 'medium' | 'high';
    profitability: 'low' | 'medium' | 'high';
  };
}

interface ConversionInsights {
  metrics: {
    freeToProConversion: {
      currentRate: number;
      targetRate: number;
      improvement: number;
      mainBarriers: string[];
      optimizationStrategies: string[];
    };
    tierComparisonViews: {
      totalViews: number;
      calculatorUsage: number;
      conversionRate: number;
    };
  };
  marketInsights: {
    paymentPreferences: {
      method: string;
      preference: number;
      conversionRate: number;
    }[];
    deviceUsage: {
      mobile: number;
      desktop: number;
    };
    timeOfDay: {
      morning: number;
      afternoon: number;
      evening: number;
      night: number;
    };
  };
  recommendations: string[];
  timestamp: string;
}

// Response interface for API calls
interface APIResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

// Hooks
export const useTierComparisonData = () => {
  const [data, setData] = useState<TierComparisonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTierComparisonData = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiUrl = getAPIUrl();
        const response = await fetch(`${apiUrl}/api/payments/tier-comparison`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch tier comparison data: ${response.status}`);
        }

        const result = await response.json() as APIResponse<TierComparisonData>;
        
        if (result.success) {
          setData(result.data);
        } else {
          throw new Error(result.error ?? 'Failed to fetch tier comparison data');
        }
      } catch (err) {
        console.error('Error fetching tier comparison data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    void fetchTierComparisonData();
  }, []);

  return { data, loading, error };
};

export const useROICalculation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculateROI = async (params: {
    accountSize: number;
    riskPerTrade: number;
    tradingDays: number;
    planId: string;
  }): Promise<ROICalculation | null> => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = getAPIUrl();
      const response = await fetch(`${apiUrl}/api/payments/calculate-roi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to calculate ROI: ${response.status}`);
      }

      const result = await response.json() as APIResponse<ROICalculation>;
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error ?? 'Failed to calculate ROI');
      }
    } catch (err) {
      console.error('Error calculating ROI:', err);
      setError(err instanceof Error ? err.message : 'Failed to calculate ROI');
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { calculateROI, loading, error };
};

export const useConversionInsights = () => {
  const [data, setData] = useState<ConversionInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConversionInsights = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiUrl = getAPIUrl();
        const response = await fetch(`${apiUrl}/api/payments/conversion-insights`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch conversion insights: ${response.status}`);
        }

        const result = await response.json() as APIResponse<ConversionInsights>;
        
        if (result.success) {
          setData(result.data);
        } else {
          throw new Error(result.error ?? 'Failed to fetch conversion insights');
        }
      } catch (err) {
        console.error('Error fetching conversion insights:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch insights');
      } finally {
        setLoading(false);
      }
    };

    void fetchConversionInsights();
  }, []);

  return { data, loading, error };
};

type TrackingData = Record<string, string | number | boolean | undefined>

export const useTierComparisonTracking = () => {
  const { user } = useAuth();

  const track = (action: 'view' | 'calculate' | 'compare' | 'convert', planId?: string, data?: TrackingData) => {
    try {
      const apiUrl = getAPIUrl();
      
      // Don't wait for the tracking call to complete
      void fetch(`${apiUrl}/api/payments/track/tier-comparison`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          planId,
          userId: user?.uid ?? 'anonymous',
          data
        })
      }).catch(err => {
        console.warn('Failed to track tier comparison:', err);
        // Don't throw error - tracking failures shouldn't break user experience
      });
    } catch (err) {
      console.warn('Failed to track tier comparison:', err);
      // Don't throw error - tracking failures shouldn't break user experience
    }
  };

  return { track };
};